// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ProjectInviteDialog.test.tsx
import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { Avatar, Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { AskPunttInvitePicker } from "./AskPunttInvitePicker";

import { useUI } from "../../contexts/ui";
import { useAnalytics } from "../../utils/analytics";
import {
  captureAskPunttInviteSent,
  type AskPunttInviteRecipientTier,
} from "../../utils/askPunttInviteAnalytics";
import { type InviteTier } from "../../utils/askPunttInviteCandidate";
import { useAppSelector } from "../../utils/hooks";
import { useAddProjectMembers } from "../../utils/queries/askPunttProjects";

/**
 * Owner-facing "+ Invite" modal for an Ask Puntt project. Uses the shared
 * tiered invite picker (project → enterprise → new email) and emits a single
 * PostHog event per successful invitation batch.
 */
export function ProjectInviteDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  members,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName?: string;
  members: AskPunttChatUserSummary[];
}) {
  const { t } = useTranslation("ui");
  const { t: tDialogs } = useTranslation("dialogs");
  const { notify } = useUI();
  const { capture } = useAnalytics("ProjectInviteDialog");
  const authUser = useAppSelector((state) => state.auth.value);
  const addMembers = useAddProjectMembers(projectId);

  const trackInvite = (
    response: Awaited<ReturnType<typeof addMembers.mutateAsync>>,
    tiers: AskPunttInviteRecipientTier[],
  ) => {
    if (!authUser?.enterpriseId || !authUser.userID) return;
    captureAskPunttInviteSent(capture, {
      senderUserId: authUser.userID,
      senderEmail: authUser.email,
      recipientUserIds: response.inviteSummary?.recipientUserIds ?? [],
      recipientEmails: response.inviteSummary?.recipientEmails ?? [],
      recipientTiers: response.inviteSummary?.recipientTiers ?? tiers,
      enterpriseId: authUser.enterpriseId,
      enterpriseName: authUser.enterpriseName,
      projectId,
      projectName,
      surface: "project_dialog",
    });
  };

  const handleInviteUser = async (
    user: AskPunttChatUserSummary,
    tier: InviteTier,
  ) => {
    if (tier === "new_email") return;
    try {
      const response = await addMembers.mutateAsync({ userIds: [user._id] });
      trackInvite(response, [tier]);
      notify({
        title: "Invited",
        message: `Added ${user.name || user.email || "user"} to the project.`,
      });
    } catch {
      // useAddProjectMembers shows an error toast; keep the dialog open.
    }
  };

  const handleInviteEmail = async (email: string) => {
    try {
      const response = await addMembers.mutateAsync({ emails: [email] });
      trackInvite(response, ["new_email"]);
      notify({
        title: "Invitation sent",
        message: `They'll receive an email to join your enterprise and this project.`,
      });
    } catch {
      // Error toast handled by mutation hook.
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="500px"
        data-testid="project-invite-dialog"
        aria-labelledby="project-invite-title"
      >
        <Dialog.Title id="project-invite-title">
          {t("inviteToProject")}
        </Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {projectName
            ? t("projectInviteDialog.addPeopleToNamedProject", { projectName })
            : t("inviteToProjectDescription")}
        </Dialog.Description>

        <AskPunttInvitePicker
          open={open}
          mode="project"
          searchInputId="project-invite-search-input"
          searchTestId="project-invite-search-input"
          resultsTestId="project-invite-results"
          loadingTestId="project-invite-results-loading"
          emptyTestId="project-invite-results-empty"
          resultRowTestIdPrefix="project-invite-result-"
          addButtonTestIdPrefix="project-invite-add-"
          emailInviteTestId="project-invite-email-row"
          accessMembers={members}
          projectMembers={members}
          isPending={addMembers.isPending}
          onInviteUser={handleInviteUser}
          onInviteEmail={handleInviteEmail}
        />

        <Flex direction="column" gap="1" mt="4">
          <Text size="2" weight="medium" id="project-invite-members-heading">
            {tDialogs("projectInvite.membersHeading")}
          </Text>
          {members.length === 0 ? (
            <Text
              size="2"
              color="gray"
              data-testid="project-invite-members-empty"
            >
              {tDialogs("projectInvite.noMembers")}
            </Text>
          ) : (
            <Flex
              direction="column"
              gap="1"
              role="list"
              aria-labelledby="project-invite-members-heading"
            >
              {members.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`project-invite-member-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  <Text size="2" weight="medium" className="truncate">
                    {user.name?.trim() || user.email?.trim() || user._id}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            data-testid="project-invite-done-button"
            onClick={() => onOpenChange(false)}
          >
            {t("shareAskPunttChatDialog.doneButton")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
